import { useCallback, useMemo } from 'react'

import { useMutation } from '@apollo/client'
import { usePostHog } from 'posthog-js/react'

import { getUserName } from '../utils'

import { useProject } from '@/context/projectContext'
import { useUser } from '@/context/userContext'
import { REGISTER_AI_TOOLS_USAGE } from '@/modules/settings/graphQL'

import {
  RegisterAiToolsUsageUiMutation,
  RegisterAiToolsUsageUiMutationVariables,
} from 'types/api.graphql'

export const useRegisterAIToolUsage = (tool: string) => {
  const { project } = useProject()
  const { user } = useUser()
  const posthog = usePostHog()

  const [register] = useMutation<
    RegisterAiToolsUsageUiMutation,
    RegisterAiToolsUsageUiMutationVariables
  >(REGISTER_AI_TOOLS_USAGE)

  // if no projects its all projects, if not its the name of the project
  const projectContext = useMemo(() => {
    return `on ${project?.name || 'all projects'}`
  }, [project?.name])

  const registerUsage = useCallback(
    async (action: string, info?: string) => {
      try {
        posthog.capture('ai_tool_usage', {
          uniqueFilter: `${tool}-${action}`,
          tool,
          action,
          project: project?.name ?? 'all projects',
          username: getUserName(user),
          info,
        })
        await register({
          variables: {
            tool,
            action: `${action} ${projectContext}. Info: ${info}`,
          },
        })
      } catch (e) {
        console.error(e)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project?.name, projectContext, register, tool, user]
  )

  return { registerUsage }
}
